import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { cn } from '@immo/ui-kit';
import type { FleetProfileCapacity, FleetProfileStatus } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { FleetStateBadge, FleetStateCircle } from './fleet-badges';
import { StatusFields } from './status-fields';
import { LaneEngine, LaneRotation } from './lane-engine';

type Props = {
  readonly profile: FleetProfileStatus;
  readonly stale: boolean;
  /** The lane's fleet-wide cap, or null when it has none. */
  readonly capacity?: FleetProfileCapacity | null;
  /** The command footer (IMM-910.8) — emitted by this card, never as a sibling block below the list (BL-112). */
  readonly footer?: ReactNode;
  /** The lane's live job — heartbeat, progress and suspect flag (fleet-agent ADR 0012) — when its worker holds one. */
  readonly pulse?: ReactNode;
  /** The open log viewer, when one is open for this profile. */
  readonly children?: ReactNode;
};

/**
 * One profile: agent-chosen `profile` name, translated `state` badge,
 * agent-chosen `title`, `fields` in report order, agent-chosen `badges` —
 * everything past the state badge is rendered exactly as reported (ADR 0047).
 */
export function ProfileCard({ profile, stale, capacity = null, pulse, footer, children }: Props) {
  const t = useTranslations('pages.admin.fleet');
  const intl = useIntl();

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-surface shadow-sm">
      <div className={cn('p-4 sm:p-5', stale && 'opacity-55')}>
        {/* Below `sm` the head wraps onto two rows — name + state, then badges
            + the report time, which `w-full` pushes onto its own line. All four
            are load-bearing and none is the one to drop at 390px, so the row
            breaks instead. One DOM either way: a phone copy beside a desktop
            copy is read twice by a screen reader. */}
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:flex-nowrap sm:gap-3">
          <FleetStateCircle state={profile.state} className="h-2.5 w-2.5" />
          <span className="shrink-0 font-mono text-base-sm font-medium text-text-primary">{profile.profile}</span>
          <FleetStateBadge state={profile.state} />
          <span className="hidden flex-1 sm:block" />
          <span className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:gap-3">
            {profile.badges.map((badge) => (
              <span
                key={badge}
                className="rounded-sm bg-neutral-100 px-1.5 py-0.5 font-mono text-xs text-text-tertiary"
              >
                {badge}
              </span>
            ))}
            <span className="whitespace-nowrap text-xs text-text-tertiary sm:text-sm">
              {t('reported-at')} {intl.formatDate(profile.reportedAt, { dateStyle: 'short', timeStyle: 'short' })}
            </span>
          </span>
        </div>

        {profile.title !== null && (
          <div className="mt-3 text-base font-medium text-text-primary sm:mt-2 sm:truncate sm:text-md">
            {profile.title}
          </div>
        )}

        {profile.fields.length > 0 && (
          <div className="mt-3">
            <StatusFields fields={profile.fields} />
          </div>
        )}

        {(profile.worker != null || capacity !== null) && (
          <LaneWorker worker={profile.worker ?? null} capacity={capacity} running={profile.state !== 'stopped'} />
        )}

        {pulse !== undefined && <div className="mt-3">{pulse}</div>}

        {/* A host with cross-engine rotation reports its pin and running engine; one before it, the engine facts;
            one before fleet-agent 0.19.0, neither — nothing to show, not "claude". */}
        {profile.engineRotation != null ? (
          <div className="mt-3">
            <LaneRotation rotation={profile.engineRotation} />
          </div>
        ) : (
          profile.engine != null && (
            <div className="mt-3">
              <LaneEngine engine={profile.engine} />
            </div>
          )
        )}

        {children !== undefined && <div className="mt-3">{children}</div>}
      </div>

      {footer}
    </section>
  );
}

/**
 * The worker that owns the lane, and how many machines run it against its
 * fleet-wide cap. `running` counts this machine in: the capacity read names
 * only the OTHER machines.
 */
function LaneWorker({
  worker,
  capacity,
  running,
}: {
  readonly worker: string | null;
  readonly capacity: FleetProfileCapacity | null;
  readonly running: boolean;
}) {
  const t = useTranslations('pages.admin.fleet');
  const count = capacity === null ? 0 : capacity.runningOn.length + (running ? 1 : 0);

  return (
    <div className="mt-3 flex min-w-0 flex-wrap gap-x-4 gap-y-1">
      {worker !== null && (
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="text-sm text-text-tertiary">{t('worker')}</span>
          <span className="min-w-0 break-words font-mono text-base-sm text-text-primary">{worker}</span>
        </span>
      )}
      {capacity !== null && (
        <span className="flex items-baseline gap-2">
          <span className="text-sm text-text-tertiary">{t('capacity-label')}</span>
          <span
            className={cn(
              'font-mono text-base-sm',
              count >= capacity.maxWorkers ? 'text-warning-700' : 'text-text-primary',
            )}
          >
            {t('capacity', { running: String(count), max: String(capacity.maxWorkers) })}
          </span>
        </span>
      )}
    </div>
  );
}
